<template>
    <div class="flex flex-col gap-8 sm:gap-16">
        <div
            v-for="(video, index) in videosArray"
            :key="index"
            class="flex justify-center"
        >
            <div
                class="w-full max-w-3xl rounded-2xl overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                @click="openYoutube(video.url)"
            >
                <div class="relative w-full h-0 pb-[56.25%] overflow-hidden">
                    <img
                        :src="
                            video.image || getYoutubeThumbnail(video.url, 'max')
                        "
                        alt=""
                        class="absolute inset-0 w-full h-full object-cover"
                        @error="
                            (event) => {
                                (event.target as HTMLImageElement).src =
                                    getYoutubeThumbnail(video.url, 'hq');
                            }
                        "
                    />
                    <div
                        class="absolute inset-0 bg-black/20 hover:bg-black/40 transition duration-200"
                    />
                    <!-- Play Icon -->
                    <div
                        class="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                        <svg
                            class="w-14 h-14 sm:w-16 sm:h-16 text-white drop-shadow-lg"
                            viewBox="0 0 60 60"
                            aria-hidden="true"
                        >
                            <path
                                fill="currentColor"
                                d="m45.563 29.174-22-15A1 1 0 0 0 22 15v30a.999.999 0 0 0 1.563.826l22-15a1 1 0 0 0 0-1.652zM24 43.107V16.893L43.225 30 24 43.107z"
                            />
                            <path
                                fill="currentColor"
                                d="M30 0C13.458 0 0 13.458 0 30s13.458 30 30 30 30-13.458 30-30S46.542 0 30 0zm0 58C14.561 58 2 45.439 2 30S14.561 2 30 2s28 12.561 28 28-12.561 28-28 28z"
                            />
                        </svg>
                    </div>
                </div>

                <div
                    v-if="video.title || video.subtitle"
                    class="text-gray-800 p-3 sm:p-4 flex flex-col justify-start"
                >
                    <div
                        v-if="video.title"
                        class="font-secondary-heading text-base sm:text-lg lg:text-2xl font-light"
                    >
                        {{ video.title }}
                    </div>
                    <div
                        v-if="video.subtitle"
                        class="text-xs sm:text-sm opacity-80 mb-1 leading-snug line-clamp-2"
                    >
                        {{ video.subtitle }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <nav
            v-if="lastPage > 1"
            class="mt-8 flex items-center justify-center gap-1"
            aria-label="Videos pagination"
        >
            <button
                type="button"
                class="px-3 py-2 text-sm font-accent text-gray-500 transition-colors hover:text-accent disabled:cursor-not-allowed disabled:opacity-30"
                :disabled="currentPage <= 1"
                @click="goToPage(currentPage - 1)"
            >
                Назад
            </button>

            <button
                v-for="page in pages"
                :key="page"
                type="button"
                class="mx-0.5 flex h-9 w-9 items-center justify-center border text-sm transition-colors"
                :class="
                    page === currentPage
                        ? 'border-accent bg-accent text-white'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-accent hover:text-accent'
                "
                @click="goToPage(page)"
            >
                {{ page }}
            </button>

            <button
                type="button"
                class="px-3 py-2 text-sm font-accent text-gray-500 transition-colors hover:text-accent disabled:cursor-not-allowed disabled:opacity-30"
                :disabled="currentPage >= lastPage"
                @click="goToPage(currentPage + 1)"
            >
                Вперёд
            </button>
        </nav>
    </div>

    <Teleport to="body">
        <div
            v-if="activeVideo"
            class="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            @click.self="closeModal"
        >
            <div class="w-full max-w-4xl aspect-video relative">
                <iframe
                    class="absolute inset-0 w-full h-full rounded-lg"
                    :src="`https://www.youtube.com/embed/${getYoutubeId(activeVideo.url)}?autoplay=1`"
                    frameborder="0"
                    allow="autoplay; encrypted-media"
                    allowfullscreen
                ></iframe>
                <button
                    class="absolute top-2 right-2 text-white text-2xl"
                    @click="closeModal"
                >
                    ✕
                </button>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import {
    defineProps,
    ref,
    onMounted,
    onBeforeUnmount,
    computed,
    type Ref,
} from "vue";

interface VideoItem {
    title: string;
    subtitle: string;
    url: string;
    image?: string;
}

const props = defineProps<{
    videos: Record<string, VideoItem>;
    currentPage: number;
    lastPage: number;
}>();

const videosArray = computed<VideoItem[]>(() => Object.values(props.videos));

const currentPage = computed(() => props.currentPage);
const lastPage = computed(() => props.lastPage);

const pages = computed<number[]>(() =>
    Array.from({ length: lastPage.value }, (_v, index) => index + 1),
);

const getYoutubeId = (url: string): string => {
    try {
        const parsedUrl = new URL(url);
        if (parsedUrl.hostname.includes("youtu.be")) {
            return parsedUrl.pathname.slice(1);
        }
        if (parsedUrl.hostname.includes("youtube.com")) {
            return parsedUrl.searchParams.get("v") || "";
        }
    } catch {
        return "";
    }
    return "";
};

const getYoutubeThumbnail = (
    url: string,
    quality: "max" | "hq" | "sd" | "mq" = "max",
): string => {
    const videoId = getYoutubeId(url);
    if (!videoId) return "";

    const qualityMap: Record<typeof quality, string> = {
        max: "maxresdefault",
        hq: "hqdefault",
        sd: "sddefault",
        mq: "mqdefault",
    };

    return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
};

const activeVideo: Ref<VideoItem | null> = ref(null);

const openYoutube = (url: string) => {
    const video = videosArray.value.find((videoItem) => videoItem.url === url);
    if (video) activeVideo.value = video;
};

const closeModal = () => {
    activeVideo.value = null;
};

const handleEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape") closeModal();
};

const goToPage = (page: number) => {
    if (page < 1 || page > lastPage.value) return;

    const url = new URL(window.location.href);
    url.searchParams.set("page", String(page));
    window.location.href = url.toString(); // updates URL + reloads page
};

onMounted(() => window.addEventListener("keydown", handleEscape));
onBeforeUnmount(() => window.removeEventListener("keydown", handleEscape));
</script>
